import React, { useEffect, useState } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { documentsService, partnersService } from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react/dist/iconify.js';

const DocumentsListPage = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    partner_id: '',
    category: '',
    search: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });

  useEffect(() => {
    loadPartners();
    loadDocuments();
  }, [pagination.page, pagination.limit, filters]);

  const loadPartners = async () => {
    try {
      const response = await partnersService.getAllPartners({ 
        status: 'approved',
        limit: 1000 
      });
      if (response.success) {
        setPartners(response.data || []);
      }
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await documentsService.getAllDocuments(params);

      if (response.success) {
        setDocuments(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        toast.error(response.error || 'Dökümanlar yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Dökümanlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (documentId, title) => {
    if (!window.confirm(`"${title}" dökümanını silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      const response = await documentsService.deleteDocument(documentId);
      if (response.success) {
        toast.success('Döküman başarıyla silindi');
        loadDocuments();
      } else {
        toast.error(response.error || 'Döküman silinemedi');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Döküman silinirken bir hata oluştu');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getFileIcon = (fileType) => {
    const icons = {
      pdf: 'solar:file-text-outline',
      doc: 'solar:file-text-outline',
      docx: 'solar:file-text-outline',
      xls: 'solar:file-text-outline',
      xlsx: 'solar:file-text-outline',
      jpg: 'solar:gallery-outline',
      jpeg: 'solar:gallery-outline',
      png: 'solar:gallery-outline',
      gif: 'solar:gallery-outline'
    };
    return icons[fileType] || 'solar:file-outline';
  };

  const getDocumentUrl = (filePath) => {
    // DigitalOcean Spaces URL'i oluştur
    const spacesEndpoint = process.env.REACT_APP_SPACES_ENDPOINT || 'https://randuapp.nyc3.digitaloceanspaces.com';
    return `${spacesEndpoint}/${filePath}`;
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Dökümanlar" />
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">Döküman Listesi</h5>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/documents/upload')}
          >
            <Icon icon="solar:upload-outline" className="me-2" />
            Döküman Yükle
          </button>
        </div>
        <div className="card-body">
          {/* Filtreler */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <label className="form-label">Partner</label>
              <select
                className="form-select"
                value={filters.partner_id}
                onChange={(e) => handleFilterChange('partner_id', e.target.value)}
              >
                <option value="">Tümü</option>
                {partners.map(partner => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Kategori</label>
              <select
                className="form-select"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">Tümü</option>
                <option value="general">Genel</option>
                <option value="contract">Sözleşme</option>
                <option value="invoice">Fatura</option>
                <option value="report">Rapor</option>
                <option value="other">Diğer</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Arama</label>
              <input
                type="text"
                className="form-control"
                placeholder="Başlık, açıklama ara..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Sıralama</label>
              <select
                className="form-select"
                value={`${filters.sortBy}_${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('_');
                  setFilters(prev => ({ ...prev, sortBy, sortOrder }));
                }}
              >
                <option value="created_at_DESC">Tarih (Yeni → Eski)</option>
                <option value="created_at_ASC">Tarih (Eski → Yeni)</option>
                <option value="title_ASC">Başlık (A → Z)</option>
                <option value="title_DESC">Başlık (Z → A)</option>
              </select>
            </div>
          </div>

          {/* Tablo */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : documents.length === 0 ? (
            <div className="alert alert-info text-center">
              Döküman bulunamadı.
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Döküman</th>
                      <th>Partner</th>
                      <th>Kategori</th>
                      <th>Dosya Boyutu</th>
                      <th>Oluşturulma</th>
                      <th className="text-end">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map(document => (
                      <tr key={document.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <Icon 
                              icon={getFileIcon(document.file_type)} 
                              className="me-2 text-primary"
                              style={{ fontSize: '24px' }}
                            />
                            <div>
                              <strong>{document.title}</strong>
                              {document.description && (
                                <div className="text-muted small">{document.description}</div>
                              )}
                              <div className="text-muted small">{document.file_name}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {document.partner_name ? (
                            <div>
                              <strong>{document.partner_name}</strong>
                              {document.partner_phone && (
                                <div className="text-muted small">{document.partner_phone}</div>
                              )}
                            </div>
                          ) : (
                            <span className="badge bg-info">Tüm Partnerler</span>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-secondary">{document.category || 'general'}</span>
                        </td>
                        <td>{formatFileSize(document.file_size)}</td>
                        <td>{formatDate(document.created_at)}</td>
                        <td className="text-end">
                          <a
                            href={getDocumentUrl(document.file_path)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary me-2"
                          >
                            <Icon icon="solar:download-outline" className="me-1" />
                            İndir
                          </a>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(document.id, document.title)}
                          >
                            <Icon icon="solar:trash-bin-outline" className="me-1" />
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto', display: 'inline-block' }}
                    value={pagination.limit}
                    onChange={handlePageSizeChange}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="ms-2 text-muted">
                    Toplam {pagination.total} kayıt
                  </span>
                </div>
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                      >
                        Önceki
                      </button>
                    </li>
                    {[...Array(pagination.totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === pagination.totalPages ||
                        (page >= pagination.page - 2 && page <= pagination.page + 2)
                      ) {
                        return (
                          <li key={page} className={`page-item ${pagination.page === page ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </li>
                        );
                      } else if (
                        page === pagination.page - 3 ||
                        page === pagination.page + 3
                      ) {
                        return (
                          <li key={page} className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        );
                      }
                      return null;
                    })}
                    <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        Sonraki
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>
    </MasterLayout>
  );
};

export default DocumentsListPage;

